import { FC, useEffect, useRef, useState } from 'react'
import { Tooltip } from 'src/components/Tooltip'
import styles from './truncatedTooltip.module.scss'

interface TruncatedTooltipProps {
  className?: string
  maxLines?: number
  text: string
}

export const TruncatedTooltip: FC<TruncatedTooltipProps> = ({
  className,
  maxLines = 1,
  text,
}) => {
  const ref = useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return

    const updateTruncation = () => {
      // Ignore measurements of a node that isn't laid out yet (detached, or a
      // StrictMode mount/unmount transient): scrollHeight/clientHeight are both
      // 0 then, which would wrongly read as "not truncated".
      if (
        !node.isConnected ||
        (node.clientHeight === 0 && node.clientWidth === 0)
      ) {
        return
      }

      setIsTruncated(
        maxLines > 1
          ? node.scrollHeight - node.clientHeight > 1
          : node.scrollWidth - node.clientWidth > 1
      )
    }

    // Measure now and on the next frame (after layout settles).
    updateTruncation()
    const rafId = requestAnimationFrame(updateTruncation)

    // A clamped box keeps the SAME width and height when its text reflows from
    // 2 → N lines, so ResizeObserver never fires when a late-loading web font
    // (e.g. non-Latin glyphs) changes the wrapped line count. `fonts.ready` is
    // a one-shot promise that can resolve before such a font is done, so also
    // re-measure on every `loadingdone` — that's the only reliable late signal.
    const fonts = typeof document !== 'undefined' ? document.fonts : undefined
    fonts?.ready?.then(updateTruncation).catch(() => undefined)
    fonts?.addEventListener?.('loadingdone', updateTruncation)

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateTruncation)
        : null

    if (resizeObserver) {
      resizeObserver.observe(node)
    }

    window.addEventListener('resize', updateTruncation)

    return () => {
      cancelAnimationFrame(rafId)
      fonts?.removeEventListener?.('loadingdone', updateTruncation)
      resizeObserver?.disconnect()
      window.removeEventListener('resize', updateTruncation)
    }
  }, [maxLines, text])

  if (!isTruncated) {
    return (
      <span className={className} ref={ref}>
        {text}
      </span>
    )
  }

  return (
    <Tooltip
      mouseEnterDelay={0.1}
      overlay={text}
      overlayClassName="truncated-tooltip"
      overlayInnerClassName={styles.tooltipInner}
      placement="top"
    >
      <span className={className} ref={ref}>
        {text}
      </span>
    </Tooltip>
  )
}
